/**
 * Shared Decision Making types — F-04 (TB-2)
 *
 * Extracted from DecisionRightsPage.tsx + DecisionMakingPanel.tsx
 * to eliminate verbatim duplication.  Import from here instead of
 * re-defining locally.
 *
 * Mirrors server/modules/governance/governance.types.ts
 * (DecisionRightsLevel, DecisionRightsMatrix, Decider).
 */

// ─── Decider union ─────────────────────────────────────────────────────────

/**
 * Structured decider object (TB-2 AC-DM-04/05/06).
 * Must stay in sync with the backend `Decider` type.
 *
 *  - role:   chips from the org's Role Contracts; ref = role-contract id
 *  - actor:  chips from the org's Actors; ref = actor id
 *  - custom: free-text off-system parties (owners, external counsel, etc.)
 */
export type Decider =
  | { kind: 'role';   ref: string; label: string }
  | { kind: 'actor';  ref: string; label: string }
  | { kind: 'custom';             label: string };

// ─── Matrix types ──────────────────────────────────────────────────────────

export interface DecisionRightsLevel {
  actors: Decider[];
  mode: string;
  notes?: string;
}

export type ImpactLevel = 'root' | 'trunk' | 'branch' | 'leaf';

export interface DecisionRightsMatrix {
  root: DecisionRightsLevel;
  trunk: DecisionRightsLevel;
  branch: DecisionRightsLevel;
  leaf: DecisionRightsLevel;
}

// ─── Default matrix ────────────────────────────────────────────────────────

/** Mirrors DEFAULT_DECISION_RIGHTS_MATRIX on the server. */
export const DEFAULT_MATRIX: DecisionRightsMatrix = {
  root: {
    actors: [{ kind: 'custom', label: 'CEO' }],
    mode: 'consultative',
    notes: 'CEO only, with Advisory Board consultation',
  },
  trunk: {
    actors: [
      { kind: 'custom', label: 'CEO' },
      { kind: 'custom', label: 'Domain VP' },
    ],
    mode: 'consultative',
    notes: 'CEO + Domain VPs (consultative)',
  },
  branch: {
    actors: [{ kind: 'custom', label: 'Domain VP' }],
    mode: 'delegation',
    notes: 'Domain VP (or AI Operator in Copilot/Autopilot with approval)',
  },
  leaf: {
    actors: [{ kind: 'custom', label: 'Any actor with roros.decisions.create' }],
    mode: 'autocratic',
    notes: 'Any actor with roros.decisions.create (or AI Operator in Copilot/Autopilot autonomously)',
  },
};
